import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Chip, FormControlLabel, Link, Switch } from '@mui/material';
import { FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as XlsxIcon } from '@mui/icons-material';
import { TrialBalance as ITrialBalance } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate, firstOfYearLocalIso } from '../../utils/dateUtils';
import { downloadCsv } from '../../utils/csv';
import { trialBalanceCsv } from '../../utils/financialReportCsv';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
    ASSET: 'primary',
    LIABILITY: 'warning',
    EQUITY: 'secondary',
    REVENUE: 'success',
    EXPENSE: 'default',
};

const formatAmount = (value: number | null | undefined) =>
    (value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const firstOfYear = firstOfYearLocalIso(today);
const todayStr = toLocalIsoDate(today);

const TrialBalance: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<ITrialBalance | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [from, setFrom] = useState(firstOfYear);
    const [to, setTo] = useState(todayStr);
    // Default = post-closing (year-end closing entries included); off = pre-closing.
    const [postClosing, setPostClosing] = useState(true);
    // The toggle value the report on screen was loaded with; exports use THIS, not the unapplied switch.
    const [reportPostClosing, setReportPostClosing] = useState(true);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const data = await AccountService.getTrialBalance(from, to, postClosing);
            setReport(data);
            setReportPostClosing(postClosing);
        } catch (err) {
            toast.showApiError(err, 'Failed to load trial balance');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Exports the report on screen (its own from/to), never the unapplied date inputs.
    const handleExport = () => {
        if (!report) return;
        const { headers, rows } = trialBalanceCsv(report);
        downloadCsv(`trial-balance-${report.fromDate}-to-${report.toDate}.csv`, headers, rows);
    };

    const handleDownload = async (format: 'pdf' | 'xlsx') => {
        if (!report) return;
        try {
            setDownloading(true);
            await AccountService.downloadTrialBalance(report.fromDate, report.toDate, format, reportPostClosing);
        } catch (err) {
            toast.showApiError(err, 'Failed to download the trial balance');
        } finally {
            setDownloading(false);
        }
    };

    const closingBalanced = !!report
        && Math.abs((report.closingTotalDebits ?? 0) - (report.closingTotalCredits ?? 0)) < 0.005;

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.trialBalance.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>Trial Balance</h2>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label data-help="accounting.trialBalance.from" style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label data-help="accounting.trialBalance.to" style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <FormControlLabel
                    data-help="accounting.trialBalance.closing"
                    control={<Switch checked={postClosing} onChange={e => setPostClosing(e.target.checked)} />}
                    label={postClosing ? 'Post-closing (includes year-end closing entries)' : 'Pre-closing (year-end closing entries left out)'}
                />
                <Button data-help="accounting.trialBalance.apply" variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
                <Button data-help="accounting.trialBalance.exportCsv"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || !report || report.lines.length === 0}
                >
                    Export CSV
                </Button>
                <Button data-help="accounting.trialBalance.exportPdf" variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownload('pdf')}
                    disabled={loading || downloading || !report || report.lines.length === 0}>
                    PDF
                </Button>
                <Button data-help="accounting.trialBalance.exportXlsx" variant="outlined" startIcon={<XlsxIcon />} onClick={() => handleDownload('xlsx')}
                    disabled={loading || downloading || !report || report.lines.length === 0}>
                    Excel
                </Button>
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem', marginBottom: '12px' }}>
                Opening = balance before the From date; Debits/Credits = activity in the period;
                Closing = balance at the To date. Balances are shown on each account's normal side.
            </div>

            <ScrollableTable>
            <table data-help-section="accounting.trialBalance.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.trialBalance.col.code" style={tableHeaderStyle}>Code</th>
                        <th data-help="accounting.trialBalance.col.name" style={tableHeaderStyle}>Account Name</th>
                        <th data-help="accounting.trialBalance.col.type" style={tableHeaderStyle}>Type</th>
                        <th data-help="accounting.trialBalance.col.opening" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Opening</th>
                        <th data-help="accounting.trialBalance.col.debits" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Debits</th>
                        <th data-help="accounting.trialBalance.col.credits" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Credits</th>
                        <th data-help="accounting.trialBalance.col.closing" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Closing</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={7} style={{ ...tableCellStyle, textAlign: 'center' }}>Loading...</td></tr>
                    )}
                    {!loading && (!report || report.lines.length === 0) && (
                        <tr>
                            <td colSpan={7} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                No posted entries in the selected period.
                            </td>
                        </tr>
                    )}
                    {!loading && report && report.lines.map((line, i) => (
                        <tr key={line.accountId} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={tableCellStyle}>{line.accountCode}</td>
                            <td style={tableCellStyle}>
                                <Link data-help="accounting.trialBalance.action.ledger"
                                    component={RouterLink}
                                    to={`/accounting/accounts/${line.accountId}/ledger?from=${report.fromDate}&to=${report.toDate}`}
                                    underline="hover"
                                    color="inherit"
                                >
                                    {line.accountName}
                                </Link>
                            </td>
                            <td style={tableCellStyle}>
                                <Chip label={line.accountType} size="small" color={TYPE_COLORS[line.accountType] ?? 'default'} />
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.openingBalance)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalDebits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalCredits)}</td>
                            <td style={{
                                ...tableCellStyle,
                                textAlign: 'right',
                                fontWeight: 500,
                                color: (line.closingBalance ?? 0) >= 0 ? '#2e7d32' : '#c62828',
                            }}>
                                {formatAmount(line.closingBalance)}
                            </td>
                        </tr>
                    ))}
                    {!loading && report && report.lines.length > 0 && (
                        <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #aaa' }}>
                            <td style={tableCellStyle} colSpan={4}>Period Total</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.grandTotalDebits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.grandTotalCredits)}</td>
                            <td style={tableCellStyle} />
                        </tr>
                    )}
                    {!loading && report && report.lines.length > 0 && (
                        <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700 }}>
                            <td style={tableCellStyle} colSpan={4}>
                                Closing balances at {report.toDate} (debit / credit)&nbsp;
                                <Chip
                                    size="small"
                                    label={closingBalanced ? 'Balanced' : 'Unbalanced'}
                                    color={closingBalanced ? 'success' : 'error'}
                                />
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.closingTotalDebits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.closingTotalCredits)}</td>
                            <td style={tableCellStyle} />
                        </tr>
                    )}
                </tbody>
            </table>
            </ScrollableTable>
        </div>
    );
};

export default TrialBalance;
